import { Box, Button, Checkbox, Divider, FormControl, Grid, ListItemIcon, Menu, MenuItem, Pagination, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, Typography } from "@mui/material";
import CircularProgress from '@mui/material/CircularProgress';
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment";
import { useEffect, useState } from "react";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { IFiltro } from "../../../core/interfaces/IFiltro";
import { useFetchInvervalosCaixa } from "../../hooks/useFetchInvervalosCaixa";
import { IIntervaloCaixa } from "../../interfaces/IIntervaloCaixa";
import { FiltrosContainer } from "./styles";
import { RelatorioCaixaTableRow } from "../../components/RelatorioCaixaTableRow";
import { NFCeTableRow } from "../../components/NFCeTableRow";
import { useNavigate } from "react-router-dom";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { useThemeContext } from "../../../core/contexts/ThemeContext";
import { useFetchNotasFiscais } from "../../hooks/useFetchNotasFiscais";
import { INFCe } from "../../interfaces/INFCe";
import { useNotasFiscais } from "../../hooks/useNotasFiscais";
import FullScreenLoading from "../../../core/components/FullScreenLoading";
import { ArrowDropDown, Cancel, Download, FileDownload } from "@mui/icons-material";
import JSZip from "jszip";
import { useMemo } from "react";
import { ApiFactory } from "../../../core/config/ApiFactory";

type DataInicio = {
    dataInicio: Moment | null;
};

type DataFim = {
    dataFim: Moment | null;
};

export const NotasFiscais = () => {
    const { dados, isLoading, filtrar, handlePaginaChange, limpar, refresh } = useFetchNotasFiscais();
    const snackbar = useSnackbar();
    const apiFinanceiro = useMemo(() => ApiFactory.getApi("MS_FINANCEIRO"), []);

    const [dataInicioState, setDataInicioState] = useState<DataInicio>({ dataInicio: null });
    const [dataFimState, setDataFimState] = useState<DataFim>({ dataFim: null });

    const handleDataInicioChange = (date: Moment | null) => {
        setDataInicioState((prevState) => ({ ...prevState, dataInicio: date }));
    };

    const handleDataFimChange = (date: Moment | null) => {
        setDataFimState((prevState) => ({ ...prevState, dataFim: date }));
    };

    const handleLimpar = () => {
        setDataInicioState({ dataInicio: null });
        setDataFimState({ dataFim: null });
        limpar();
    }

    const handleFiltrar = () => {
        const filtros: IFiltro[] = [];

        if (dataInicioState.dataInicio && !dataFimState.dataFim) {
            snackbar("Selecione a data fim para poder filtrar por período");
            return;
        }

        if (dataFimState.dataFim && !dataInicioState.dataInicio) {
            snackbar("Selecione a data de início para poder filtrar por período");
            return;
        }

        if (dataFimState.dataFim && dataInicioState.dataInicio) {
            filtros.push({
                chave: "dataInicio",
                valor: dataInicioState.dataInicio.format("YYYY-MM-DD")
            })

            filtros.push({
                chave: "dataFim",
                valor: dataFimState.dataFim.format("YYYY-MM-DD")
            })
        }

        filtrar(filtros)
    }

    const navigate = useNavigate();
    const { verificarAcessoFuncionalidade, perfil } = useAutenticacaoContext();

    useEffect(() => {
        if (perfil && !verificarAcessoFuncionalidade("Notas fiscais")) {
            navigate("/nao-autorizado")
        }
    }, [perfil])

    const { theme } = useThemeContext();

    const [selecionadas, setSelecionadas] = useState<string[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openAcoes = Boolean(anchorEl);

    const toggleSelecionado = (uuid: string) => {
        setSelecionadas(prev =>
            prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
        );
    };

    const abrirAcoes = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const fecharAcoes = () => setAnchorEl(null);

    const [loadingZip, setLoadingZip] = useState(false);
    const [loadingCancelamento, setLoadingCancelamento] = useState(false);
    const [loadingExport, setLoadingExport] = useState(false);

    const handleExportar = async () => {
        setLoadingExport(true);
        try {
            const query = new URLSearchParams();
            if (dataInicioState.dataInicio) query.append('dataInicio', dataInicioState.dataInicio.format('YYYY-MM-DD'));
            if (dataFimState.dataFim) query.append('dataFim', dataFimState.dataFim.format('YYYY-MM-DD'));

            const { data } = await apiFinanceiro.get(`/contabilidade/nfce/exportar?${query.toString()}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'notas-fiscais.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            snackbar("Ocorreu um erro ao exportar as notas fiscais", { severity: "error" });
        } finally {
            setLoadingExport(false);
        }
    };

    const baixarPDFs = async () => {
        setLoadingZip(true);
        try {
            const zip = new JSZip();
            for (const nf of dados.itens.filter(nf => selecionadas.includes(nf.uuid))) {
                try {
                    const response = await fetch(nf.pdf.url);
                    if (!response.ok) throw new Error("Erro ao baixar PDF");

                    const blob = await response.blob();
                    zip.file(nf.pdf.nome, blob);
                } catch (err) {
                    snackbar("Erro ao baixar PDF de uma nota fiscal.");
                }
            }
            const content = await zip.generateAsync({ type: 'blob' });
            const url = window.URL.createObjectURL(content);
            const a = document.createElement("a");
            a.href = url;
            a.download = "notas_pdfs.zip";
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setLoadingZip(false);
        }
    };

    const baixarXMLs = async () => {
        setLoadingZip(true);
        try {
            const zip = new JSZip();
            for (const nf of dados.itens.filter(nf => selecionadas.includes(nf.uuid))) {
                try {
                    const response = await fetch(nf.xml.url);
                    if (!response.ok) throw new Error("Erro ao baixar XML");

                    const blob = await response.blob();
                    zip.file(nf.xml.nome, blob);
                } catch (err) {
                    snackbar("Erro ao baixar XML de uma nota fiscal.");
                }
            }
            const content = await zip.generateAsync({ type: 'blob' });
            const url = window.URL.createObjectURL(content);
            const a = document.createElement("a");
            a.href = url;
            a.download = "notas_xmls.zip";
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setLoadingZip(false);
        }
    };

    const { cancelar } = useNotasFiscais();

    const cancelarNotas = async () => {
        setLoadingCancelamento(true);
        for (const uuid of selecionadas) {
            await cancelar(uuid);
        }
        setLoadingCancelamento(false);
        refresh();
        setSelecionadas([]);
    };


    return (
        <>
            {(loadingCancelamento || loadingZip) && <FullScreenLoading />}
            <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                <FiltrosContainer>
                    <Paper sx={{
                        marginBottom: "2rem",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        padding: "1rem 2rem",
                        borderRadius: 0,
                        justifyContent: "space-between",
                        boxShadow: "none",
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }}>
                        <Typography variant="h6">
                            Notas fiscais
                        </Typography>
                        <Box className="filtros-caixa-diario">
                            <DatePicker
                                label="Data início"
                                value={dataInicioState.dataInicio}
                                onChange={handleDataInicioChange}
                                format="DD/MM/YYYY"
                                slotProps={{ textField: { size: 'small' } }}
                                className="input-caixa-diario"
                            />
                            <DatePicker
                                label="Data fim"
                                value={dataFimState.dataFim}
                                onChange={handleDataFimChange}
                                format="DD/MM/YYYY"
                                slotProps={{ textField: { size: 'small' } }}
                                className="input-caixa-diario"
                            />
                            <Box>
                                <Button
                                    onClick={handleLimpar}
                                    variant="outlined"
                                    sx={{ marginRight: "10px" }}
                                >
                                    Limpar
                                </Button>
                                <Button
                                    onClick={handleFiltrar}
                                    variant="contained"
                                    sx={{ marginRight: "10px" }}
                                >
                                    Filtrar
                                </Button>
                                <Button
                                    onClick={handleExportar}
                                    variant="outlined"
                                    startIcon={loadingExport ? <CircularProgress size={16} /> : <FileDownload />}
                                    disabled={loadingExport || isLoading}
                                    sx={{ marginRight: "10px" }}
                                >
                                    Exportar
                                </Button>
                                <Button
                                    disabled={selecionadas.length <= 0}
                                    variant="contained"
                                    endIcon={
                                        <ArrowDropDown sx={{ transform: openAcoes ? 'rotate(-180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                                    }
                                    onClick={abrirAcoes}
                                >
                                    Ações
                                </Button>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={openAcoes}
                                    onClose={fecharAcoes}
                                >
                                    <MenuItem disabled>
                                        <Typography variant="inherit" noWrap>
                                            {selecionadas.length} nota{selecionadas.length !== 1 ? 's' : ''} selecionada{selecionadas.length !== 1 ? 's' : ''}
                                        </Typography>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem onClick={baixarPDFs}>
                                        <ListItemIcon><Download fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit">Baixar PDFs</Typography>
                                    </MenuItem>

                                    <MenuItem onClick={baixarXMLs}>
                                        <ListItemIcon><Download fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit">Baixar XMLs</Typography>
                                    </MenuItem>

                                    <MenuItem onClick={cancelarNotas}>
                                        <ListItemIcon><Cancel fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit">Cancelar</Typography>
                                    </MenuItem>
                                </Menu>

                            </Box>
                        </Box>
                    </Paper>
                </FiltrosContainer>

                {dados && dados.itens.length ? (
                    <>
                        <TableContainer component={Paper} sx={{ minWidth: 350, maxWidth: "90%", margin: "0 auto", overflowY: "auto" }}>
                            <Table sx={{ minWidth: 350, width: "100%" }} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                        </TableCell>
                                        <TableCell align="center">Modelo</TableCell>
                                        <TableCell align="center">Número</TableCell>
                                        <TableCell align="center">Série</TableCell>
                                        <TableCell align="center">Destinatário</TableCell>
                                        <TableCell align="center">Data/hora</TableCell>
                                        <TableCell align="center">Protocolo</TableCell>
                                        <TableCell align="center">Status</TableCell>
                                        <TableCell align="center">Mensagem Sefaz</TableCell>
                                        <TableCell align="center">CNPJ</TableCell>
                                        <TableCell align="center">Pedido</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {dados.itens.map((nfce: INFCe) => (
                                        <NFCeTableRow
                                            key={nfce.uuid}
                                            nfce={nfce}
                                            selecionado={selecionadas.includes(nfce.uuid)}
                                            toggleSelecionado={toggleSelecionado}
                                        />
                                    ))}
                                </TableBody>

                            </Table>
                            {dados && (
                                <Box sx={{ marginTop: "1rem", padding: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                                    <Typography variant="body2" sx={{ marginLeft: "1rem" }}>
                                        Total: {dados.quantidadeTotalItens} notas fiscais
                                    </Typography>
                                    <Pagination
                                        count={dados.quantidadeTotalPaginas}
                                        page={dados.paginaAtual}
                                        onChange={handlePaginaChange}
                                        color="primary"
                                        showFirstButton
                                        showLastButton
                                        siblingCount={2}
                                        boundaryCount={2}
                                    />
                                </Box>
                            )}

                        </TableContainer>
                    </>
                ) : (
                    <Typography sx={{ padding: "1rem", width: 750 }} align='center'>
                        {isLoading ? <CircularProgress /> : "Nenhuma nota fiscal encontrada"}
                    </Typography>
                )}
            </Box>
        </>
    );
}